import { useEffect, useState } from 'react';
import { versionsnummer, eingespieltText } from './version.ts';
import { zeitpunkt } from '../api.ts';

// Welcher Stand laeuft?
//
// Marvin, 23.08.2026: „wo kann ich einen versionsstand sehen? es sieht alles
// noch genauso aus wie vorher."
//
// Es gibt ZWEI Staende, und sie koennen auseinanderlaufen:
//
//   Oberflaeche  steht im Buendel, beim Bauen hineingeschrieben.
//   Server       kommt von /api/gesund.
//
// Weichen sie ab, ist der haeufigste Grund kein Fehler im System, sondern der
// Zwischenspeicher des Browsers: die API ist neu, die Seite ist alt. Das sagt
// dieser Balken dann laut, statt es den Benutzer raten zu lassen.

declare const __LIEFERUNG__: string;
declare const __GEBAUT__: string;

export const OBERFLAECHE = typeof __LIEFERUNG__ === 'string' ? __LIEFERUNG__ : 'unbekannt';
export const GEBAUT = typeof __GEBAUT__ === 'string' ? __GEBAUT__ : '';

export interface Lieferung {
  nummer: string;
  stand: string;
  was?: string;
  migrationen_bis?: string;
}

export function useStand() {
  const [server, setServer] = useState<Lieferung | null>(null);
  const [eingespielt, setEingespielt] = useState<string | null>(null);
  useEffect(() => {
    fetch('/api/gesund', { credentials: 'same-origin' })
      .then((a) => a.json())
      .then((d) => {
        setServer(d?.lieferung ?? null);
        setEingespielt(d?.eingespielt ?? null);
      })
      .catch(() => setServer(null));
  }, []);
  const einig = !server || server.nummer === OBERFLAECHE;
  return { server, oberflaeche: OBERFLAECHE, einig, eingespielt };
}

/**
 * Klein, in der Fussleiste - UND DER WEG INS ÄNDERUNGSVERZEICHNIS.
 *
 * ORIGINALTEXT MARVIN (05.09.2026)
 *   „den menüpunkt «Änderungen» machst du weg, stattdessen machst du die
 *    version unten anklickbar, da kommt dann der changelog. und die version
 *    musst du vom format was anpassen, denn da steht ja noch ein datum drin
 *    und das passt ja so garnicht. mache nur die versionsnummer und wann es
 *    eingespielt wurde."
 *
 * **DIE VERSION IST DER NATÜRLICHE ORT DAFÜR.** Wer wissen will, was sich
 * geändert hat, sieht zuerst nach, welche Version läuft - und stand danach
 * vor einem Menüpunkt in einer ganz anderen Gruppe. Ein Menüpunkt, den man
 * über die Version sucht, gehört an die Version.
 *
 * **ES IST KEIN ZWEITER WEG ZU DERSELBEN SACHE**, sondern der einzige: der
 * Menüpunkt ist mit derselben Lieferung gefallen. Zwei Wege wären eine halbe
 * Auskunft - dieselbe Lehre wie bei den Vordrucken und beim Wochenplan.
 *
 * **UND WER NICHT HINDARF, BEKOMMT KEINEN KNOPF.** Ohne `changelog.lesen`
 * steht die Version als Text da, wie bisher: **ein Knopf, der auf ein 403
 * führt, ist schlimmer als kein Knopf.** Der Riegel bleibt im Server, die
 * Anzeige ist die Höflichkeit.
 */
export function StandKlein({ auf }: { auf?: () => void } = {}) {
  const { server, einig, eingespielt } = useStand();
  const wann = eingespieltText(eingespielt);
  const inhalt = (<>
    Version {versionsnummer(OBERFLAECHE)}
    {/* DAS DATUM STEHT IN DERSELBEN SCHRIFT WIE DIE NUMMER.
        Hier stand eine Klasse `leiser`, die es in diesem Kasten gar nicht
        gibt (sie gilt nur in `.schritte`) - und wenn es sie gäbe, wäre sie
        eine halbdurchsichtige Schrift: **keine Auskunft, sondern eine
        schlechter lesbare.** Die ganze Zeile ist ohnehin `klein leise`.

        UND HIER STEHT DIE ZAHL OHNE AUFSCHRIFT, WEIL SIE SONST ABBRICHT.
        ORIGINALTEXT MARVIN (05.09.2026)
          „mache nur die versionsnummer und wann es eingespielt wurde"
          „«eingespielt am» schreibst du um in «Release Datum»"
        Beides ist gebaut, nur nicht beides an derselben Stelle. „Version 115 ·
        Release Datum 05.09.2026" braucht **219 Bildpunkte in einer Spalte von
        207** - im Browser nachgemessen, nicht gerechnet. Lieferung .115 hat
        den Umbruch abgeschafft und dafuer den Deckel bekommen: auf dem
        Bildschirm stand „Release Datum 05.09…." - **ein abgeschnittenes Datum
        ist ein halbes**, und das Jahr ist genau der Teil, der fehlte.

        DREI AUSGAENGE, UND ZWEI DAVON SIND SCHLECHTER. Zwei Zeilen sind wieder
        das, was er beanstandet hat („die ganze infos unten sollen nicht ...
        untendrunter sein"); eine dritte Schriftgroesse gibt es in diesem Haus
        nicht. Also traegt die Fussleiste, was er zuerst gesagt hat - Nummer
        und Datum -, und die AUFSCHRIFT steht dort, wo Platz dafuer ist: im
        Aenderungsverzeichnis, das genau dieser Knopf aufmacht. Nachgemessen
        braucht die kurze Form 133 von 207 Bildpunkten; auch eine vierstellige
        Nummer passt.

        UND DER TITEL DANEBEN IST SEIT .118 WEG (Marvin: „die hover mit deb
        infos soll weg"). Der ganze Satz steht im Aenderungsverzeichnis, das
        dieser Knopf aufmacht - siehe unten. */}
    {wann && <>, {wann}</>}
    {!einig && (
      <span className="merker fehler" style={{ marginLeft: 4 }}>
        Server {versionsnummer(server?.nummer)}
      </span>
    )}
  </>);
  /* DER TITEL IST WEG.
   *
   * ORIGINALTEXT MARVIN (06.09.2026, mit einem Bild des Hovers)
   *   „die hover mit deb infos soll weg"
   *
   * Hier stand „Lieferung 2026-08-26.117, Server 2026-08-26.117, Release
   * Datum 06.09.2026" als `title` - ein Kasten, der beim Darüberfahren
   * aufgeht und die Zeile darunter verdeckt.
   *
   * **ES GEHT DABEI NICHTS VERLOREN, und genau das war die Frage.** Alles
   * drei steht seit .116 im Änderungsverzeichnis, das dieser Knopf aufmacht:
   * die volle Kennung, der Stand des Servers und das Release Datum mit seiner
   * Aufschrift. Ein Titel war ohnehin die schlechtere Stelle - **auf dem
   * Telefon gibt es ihn gar nicht**, und wer eine Version melden will, hat
   * dort nichts zum Kopieren.
   *
   * WAS BLEIBT, IST DIE MELDUNG, DIE ETWAS ZU SAGEN HAT. Gehen Oberfläche und
   * Server auseinander, steht der Merker „Server …" sichtbar in der Zeile
   * (siehe oben) und der Balken oben auf der Seite sagt es laut. Das ist eine
   * Auskunft und kein Hover. */
  if (!auf) {
    return (
      <div className="stand klein leise" style={{ marginTop: 6 }}>
        {inhalt}
      </div>
    );
  }
  return (
    <button type="button" className="standknopf stand klein leise"
            onClick={auf}>
      {inhalt}
    </button>
  );
}

/**
 * Der laute Balken, wenn beide Staende nicht zusammenpassen.
 *
 * Er steht ueber allem, weil sonst jede Fehlersuche in die falsche Richtung
 * geht: man sucht den fehlenden Knopf im Programmcode, und er ist laengst da,
 * nur nicht im Browser.
 */
export function StandBalken() {
  const { server, einig } = useStand();
  const [weg, setWeg] = useState(false);
  if (einig || weg || !server) return null;
  return (
    <div className="uebernahmebalken">
      <span>
        Diese Seite stammt aus Version <b>{versionsnummer(OBERFLAECHE)}</b>,
        der Server läuft mit <b>{versionsnummer(server.nummer)}</b>. Am wahrscheinlichsten liegt eine alte
        Fassung im Zwischenspeicher des Browsers.
        {' '}Einmal <b>Strg</b> und <b>F5</b> zusammen drücken.
      </span>
      <button className="knopf" onClick={() => setWeg(true)}>Verstanden</button>
    </div>
  );
}

/** Ausfuehrlich, fuer den Systemzustand. */
export function StandKarte() {
  const { server, einig } = useStand();
  return (
    <table className="tabelle">
      <tbody>
        <tr>
          <td style={{ width: 200 }}>Oberfläche</td>
          <td className="einfarbig">{versionsnummer(OBERFLAECHE)}</td>
          <td className="klein leise">
            {/* UEBER `zeitpunkt()`. Hier stand `toLocaleString('de-DE')`
                ohne Optionen: 9.9.2026, 10:00:00 - ohne fuehrende Null,
                mit Sekunden und ohne Zeitzone. */}
            {GEBAUT ? 'gebaut am ' + zeitpunkt(GEBAUT) : ''}
          </td>
        </tr>
        <tr>
          <td>Server</td>
          <td className="einfarbig">
            {server ? versionsnummer(server.nummer) : 'antwortet nicht'}
          </td>
          <td className="klein leise">{server?.was ?? ''}</td>
        </tr>
        <tr>
          <td>Migrationen</td>
          <td className="einfarbig">
            {server?.migrationen_bis ? 'bis ' + server.migrationen_bis : 'unbekannt'}
          </td>
          <td className="klein leise">
            Was davon wirklich eingespielt ist, steht im Systemzustand unter
            "Datenherkunft".
          </td>
        </tr>
        <tr>
          <td>Passen sie zusammen?</td>
          <td colSpan={2}>
            {einig
              ? <span className="merker gruen">ja</span>
              : <span className="merker fehler">
                  nein, die Seite ist älter als der Server
                </span>}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
